import { z } from 'zod';
import { dateOfBirthSchema, isCalendarDate, normalizeName } from './people.js';

export const guardianLinkStatuses = ['pending', 'approved', 'rejected', 'revoked'] as const;
export type GuardianLinkStatus = (typeof guardianLinkStatuses)[number];

/** Stable relationship codes; the labels are translated in the catalogs. */
export const guardianRelationships = ['parent', 'legal_guardian', 'grandparent', 'other'] as const;
export type GuardianRelationship = (typeof guardianRelationships)[number];

/** What a guardian may do for the athlete once the link is approved (specification section 4). */
export const guardianCapabilityNames = [
  'viewSchedule',
  'respondRsvp',
  'viewPublishedResults',
  'viewMembership',
  'receiveNotices',
] as const;
export type GuardianCapabilityName = (typeof guardianCapabilityNames)[number];
export type GuardianCapabilities = Record<GuardianCapabilityName, boolean>;

/** A capability is granted only when it is named, so an omitted one stays off. */
const capabilitiesSchema = z
  .strictObject({
    viewSchedule: z.boolean().default(false),
    respondRsvp: z.boolean().default(false),
    viewPublishedResults: z.boolean().default(false),
    viewMembership: z.boolean().default(false),
    receiveNotices: z.boolean().default(false),
  })
  .default({
    viewSchedule: false,
    respondRsvp: false,
    viewPublishedResults: false,
    viewMembership: false,
    receiveNotices: false,
  });

const nameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));
const calendarDateSchema = z.string().refine(isCalendarDate);

const optionalContactSchema = (maximumLength: number) =>
  z
    .string()
    .trim()
    .max(maximumLength)
    .transform((value) => (value === '' ? null : value))
    .nullable();

/** A guardian who has no person record yet; the server creates it together with the link. */
const newGuardianSchema = z.strictObject({
  givenName: nameSchema,
  familyName: nameSchema,
  email: optionalContactSchema(320)
    .refine((value) => value === null || (value.length >= 3 && value.includes('@')))
    .default(null),
  phone: optionalContactSchema(40)
    .refine((value) => value === null || value.length >= 3)
    .default(null),
});

/**
 * Asks for a link; it starts as `pending` and grants nothing until a secretary approves it. Name an
 * existing person with `guardianPersonId` or describe a new one with `newGuardian`, never both.
 */
export const createGuardianLinkRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    guardianPersonId: z.uuid().optional(),
    newGuardian: newGuardianSchema.optional(),
    relationship: z.enum(guardianRelationships),
    /** Defaults to the club's current day. */
    validFrom: calendarDateSchema.optional(),
    validUntil: calendarDateSchema.nullable().default(null),
    capabilities: capabilitiesSchema,
  })
  .refine(
    (request) => (request.guardianPersonId === undefined) !== (request.newGuardian === undefined),
  )
  .refine(
    (request) =>
      request.validFrom === undefined ||
      request.validUntil === null ||
      request.validUntil >= request.validFrom,
  );
export type CreateGuardianLinkRequest = z.infer<typeof createGuardianLinkRequestSchema>;

/** Rejecting or revoking may carry a short reason that is kept in the audit trail. */
export const guardianLinkDecisionRequestSchema = z.strictObject({
  reason: z.string().trim().max(500).optional(),
});
export type GuardianLinkDecisionRequest = z.infer<typeof guardianLinkDecisionRequestSchema>;

/**
 * Changes what an approved or waiting link allows, or when it ends. Only the fields sent change;
 * `validUntil: null` removes the end date.
 */
export const updateGuardianLinkRequestSchema = z
  .strictObject({
    capabilities: z
      .strictObject({
        viewSchedule: z.boolean(),
        respondRsvp: z.boolean(),
        viewPublishedResults: z.boolean(),
        viewMembership: z.boolean(),
        receiveNotices: z.boolean(),
      })
      .partial()
      .optional(),
    validUntil: calendarDateSchema.nullable().optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((request) => request.capabilities !== undefined || request.validUntil !== undefined);
export type UpdateGuardianLinkRequest = z.infer<typeof updateGuardianLinkRequestSchema>;

/** A guardian link as a secretary sees it; contact details are for verifying the relationship. */
export interface GuardianLinkView {
  id: string;
  athleteId: string;
  athleteGivenName: string;
  athleteFamilyName: string;
  guardianPersonId: string;
  guardianGivenName: string;
  guardianFamilyName: string;
  guardianEmail: string | null;
  guardianPhone: string | null;
  /** True when the guardian already has an account in the club, so there is nobody to invite. */
  guardianHasAccount: boolean;
  relationship: GuardianRelationship;
  status: GuardianLinkStatus;
  /** Calendar days, `YYYY-MM-DD`. */
  validFrom: string;
  validUntil: string | null;
  approvedAt: string | null;
  revokedAt: string | null;
  capabilities: GuardianCapabilities;
  /** The athlete's eighteenth birthday, `YYYY-MM-DD`. */
  athleteAdultOn: string;
  /** Day a secretary renewed this guardian's access for the adult athlete, if they did. */
  adultAuthorizedOn: string | null;
  /** True while the link is approved but suspended because the athlete is an adult without renewed authorization. */
  suspendedForAdult: boolean;
  /** Row version; send it in `If-Match` when deciding on the link. */
  version: number;
}

/** A person already known as a guardian in the club, offered when linking a sibling. */
export interface GuardianCandidateView {
  personId: string;
  givenName: string;
  familyName: string;
}

/** An athlete turning eighteen soon, or who did in the last weeks, with what the secretary should do. */
export interface AdultTransitionView {
  athleteId: string;
  /** Needed to invite the athlete to take control of their own access. */
  personId: string;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  /** The eighteenth birthday, `YYYY-MM-DD`. */
  adultOn: string;
  /** Negative once the athlete is eighteen. */
  daysUntilAdult: number;
  linksAwaitingAuthorization: number;
  linksAuthorized: number;
  hasAccount: boolean;
}

export const childRequestStatuses = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export type ChildRequestStatus = (typeof childRequestStatuses)[number];

/**
 * A guardian names a child they want to follow. It grants nothing: a secretary matches it to an
 * existing athlete or creates one, and only that approval opens a guardian link.
 */
export const createChildRequestSchema = z.strictObject({
  givenName: nameSchema,
  familyName: nameSchema,
  dateOfBirth: dateOfBirthSchema,
  relationship: z.enum(guardianRelationships),
  note: z
    .string()
    .trim()
    .max(500)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null),
});
export type CreateChildRequest = z.infer<typeof createChildRequestSchema>;

/**
 * Approves a child request. Name an existing athlete with `athleteId`, or send `null` to create a
 * new athlete from the requested name and birth date; `confirmDuplicate` acknowledges a same-name,
 * same-birthday athlete that already exists. The approved link carries the given capabilities.
 */
export const approveChildRequestSchema = z.strictObject({
  athleteId: z.uuid().nullable(),
  confirmDuplicate: z.boolean().default(false),
  capabilities: capabilitiesSchema,
  reason: z.string().trim().max(500).optional(),
});
export type ApproveChildRequest = z.infer<typeof approveChildRequestSchema>;

/** A rejection always tells the guardian why. */
export const rejectChildRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(500),
});
export type RejectChildRequest = z.infer<typeof rejectChildRequestSchema>;

/** An athlete that may be the requested child: the same birth date, and the same family or given name. */
export interface ChildRequestCandidateView {
  athleteId: string;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  /** True when both names match exactly (ignoring case). */
  exactMatch: boolean;
}

export interface ChildRequestView {
  id: string;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  relationship: GuardianRelationship;
  note: string | null;
  status: ChildRequestStatus;
  decisionReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  /** The athlete the request was matched to, once approved. */
  athleteId: string | null;
  /** Row version; send it in `If-Match` when deciding or withdrawing. */
  version: number;
}

/** What a secretary sees: who asked, and which athletes may already be the child. */
export interface ChildRequestReviewView extends ChildRequestView {
  guardianPersonId: string;
  guardianGivenName: string;
  guardianFamilyName: string;
  guardianEmail: string | null;
  candidates: ChildRequestCandidateView[];
}
